<template>
  <el-dialog
    title="附件合规检测"
    :visible.sync="dialogVisible"
    width="80%"
    top="6vh"
    custom-class="compliance-check-dialog"
    append-to-body
    @close="handleClose">

    <!-- 配置态：选择扫描时间区间 -->
    <div v-if="!scanned">
      <el-alert type="info" :closable="false" style="margin-bottom: 15px;">
        <template slot="title">检测规则（仅扫未审核的收款记录，按收款日期区间）</template>
        <div class="rule-text">
          1. 品牌项目：新签合同的全款/首款，附件需至少 2 个 PDF；续签的续签款，需至少 1 个 PDF；<br>
          2. 代运营项目：新签或续签的全款/首款/续签款，附件需至少 1 个 PDF；<br>
          3. 「其他」类型项目：不检查 PDF；<br>
          4. 以上记录若收支金额大于 0，附件中还必须有 1 张支付截图（图片附件）。
        </div>
      </el-alert>

      <el-form :inline="true" size="small" label-width="90px" @submit.native.prevent>
        <el-form-item label="时间区间">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            :clearable="false"
            style="width: 300px">
          </el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="scanning" @click="startScan">开始扫描</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 结果态：扫描结果列表 -->
    <div v-else>
      <!-- 汇总条 -->
      <el-alert type="warning" :closable="false" style="margin-bottom: 10px;">
        <template slot="title">
          共扫描 {{ summary.scanned }} 条主款记录，不合规 {{ summary.nonCompliant }} 条
          <span v-if="summary.skipped > 0">（另有 {{ summary.skipped }} 条记录关联数据缺失已跳过）</span>
        </template>
      </el-alert>

      <!-- 结果表格 -->
      <el-table v-loading="scanning" :data="resultList" border size="small" max-height="420">
        <el-table-column label="#" type="index" width="45" :index="indexMethod" align="center" fixed="left"></el-table-column>

        <!-- 客户/合同 -->
        <el-table-column label="客户/合同" width="200px">
          <template slot-scope="scope">
            <el-link class="text-title" type="primary" @click="handleProjectClick(scope.row)">{{ scope.row.projectName }}</el-link>
            <div class="text-content">{{ scope.row.contractNo }}<span v-if="scope.row.contractStatus === -1" class="red">（已解约）</span></div>
          </template>
        </el-table-column>

        <!-- 收支金额 -->
        <el-table-column label="收支金额" width="140px">
          <template slot-scope="scope">
            <div class="text-content">
              <el-tag type="success" size="small">收款：¥ {{ scope.row.totalAmount }} 元</el-tag>
            </div>
          </template>
        </el-table-column>

        <!-- 收款日期 -->
        <el-table-column label="收款日期" width="100px">
          <template slot-scope="scope">
            <div class="text-content">{{ scope.row.payDate | date }}</div>
          </template>
        </el-table-column>

        <!-- 签约人 -->
        <el-table-column label="签约人" width="80px">
          <template slot-scope="scope">
            <div class="text-content">{{ scope.row.receiverName }}</div>
          </template>
        </el-table-column>

        <!-- 钱款类型 -->
        <el-table-column label="钱款类型" width="90px">
          <template slot-scope="scope">
            <div class="text-content">{{ scope.row.payType }}</div>
          </template>
        </el-table-column>

        <!-- 渠道来源 -->
        <el-table-column label="渠道来源" width="100px">
          <template slot-scope="scope">
            <div class="text-content">{{ scope.row.feeType }}</div>
          </template>
        </el-table-column>

        <!-- 收款账户 -->
        <el-table-column label="收款账户" width="150px">
          <template slot-scope="scope">
            <div class="text-content">{{ scope.row.payAccount }}</div>
          </template>
        </el-table-column>

        <!-- 附件 -->
        <el-table-column label="附件" width="200px">
          <template slot-scope="scope">
            <div v-if="scope.row.attachments && scope.row.attachments.length > 0" style="display: flex; gap: 5px; flex-wrap: wrap;">
              <div v-for="(file, index) in scope.row.attachments" :key="index" @click="handlePreviewAttachment(file)" style="cursor: pointer;">
                <img v-if="file.type === 'image'" :src="getFileUrl(file.url)" alt="附件" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px; border: 1px solid #dcdfe6;" />
                <img v-else-if="file.type === 'pdf'" :src="pdfIconUrl" alt="PDF" style="width: 40px; height: 40px; object-fit: contain; border-radius: 4px; border: 1px solid #dcdfe6;" />
                <i v-else class="el-icon-document" style="font-size: 40px; color: #909399;"></i>
              </div>
            </div>
            <div v-else class="text-content">无</div>
          </template>
        </el-table-column>

        <!-- 不合规原因 -->
        <el-table-column label="不合规原因" width="180px">
          <template slot-scope="scope">
            <div style="display: flex; flex-direction: column; gap: 4px; align-items: flex-start;">
              <el-tag v-for="(reason, index) in scope.row.reasons" :key="index" type="danger" size="small">{{ reason }}</el-tag>
            </div>
            <div class="text-content" style="margin-top: 4px; color: #909399;">PDF {{ scope.row.pdfCount }} 个 / 截图 {{ scope.row.imageCount }} 张</div>
          </template>
        </el-table-column>

        <!-- 备注 -->
        <el-table-column label="备注" min-width="200px">
          <template slot-scope="scope">
            <div class="text-content">{{ scope.row.remark || '无' }}</div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页区域 -->
      <el-pagination style="margin-top: 10px;"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagenum"
        :page-sizes="[10, 20, 50]"
        :page-size="pagesize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="resultTotal">
      </el-pagination>

      <!-- 操作按钮 -->
      <div style="margin-top: 10px; text-align: right;">
        <el-button size="small" icon="el-icon-refresh" @click="rescan">重新扫描</el-button>
        <el-button size="small" type="primary" icon="el-icon-download" :loading="exporting" :disabled="resultTotal === 0" @click="handleExport">导出结果</el-button>
      </div>
    </div>

    <!-- 图片大图预览（隐藏实例，clickHandler 触发） -->
    <el-image
      v-show="false"
      ref="imageViewer"
      :src="currentImageUrl"
      :preview-src-list="imagePreviewList">
    </el-image>

    <!-- PDF 预览对话框 -->
    <el-dialog :visible.sync="pdfPreviewDialogVisible" width="60%" :title="previewTitle" append-to-body>
      <div style="height: 600px;">
        <iframe :src="previewUrl" style="width: 100%; height: 100%; border: none;"></iframe>
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
export default {
  name: 'ComplianceCheckDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // 配置态/结果态
      scanned: false,
      scanning: false,
      exporting: false,

      // 扫描时间区间（默认当月）
      dateRange: [],

      // 结果
      resultList: [],
      resultTotal: 0,
      pagenum: 1,
      pagesize: 20,
      summary: {
        scanned: 0,
        nonCompliant: 0,
        skipped: 0
      },

      // 附件预览相关
      base: '',
      pdfIconUrl: require('@/assets/pdf.png'),
      pdfPreviewDialogVisible: false,
      previewUrl: '',
      previewTitle: '',
      currentImageUrl: '',
      imagePreviewList: []
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      }
    }
  },
  created () {
    this.dateRange = this._defaultMonthRange()
  },
  methods: {
    // 默认当月区间（本月第一天 ~ 今天）
    _defaultMonthRange () {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      return [`${y}-${m}-01`, `${y}-${m}-${d}`]
    },

    // 开始扫描（回到第一页）
    startScan () {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return this.$message.warning('请选择扫描时间区间')
      }
      this.pagenum = 1
      this.fetchPage(true)
    },

    // 请求扫描结果
    async fetchPage (isFirst = false) {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return this.$message.warning('请选择扫描时间区间')
      }
      this.scanning = true
      try {
        const { data: res } = await this.$http.get('zhaoshang/customerBill/checkCompliance', {
          params: {
            startDate: this.dateRange[0],
            endDate: this.dateRange[1],
            pagenum: this.pagenum,
            pagesize: this.pagesize
          }
        })
        if (res.meta.status !== 200) {
          return this.$message.error(res.meta.msg)
        }
        this.resultList = res.data.list
        this.resultTotal = res.data.total
        this.summary = res.data.summary || { scanned: 0, nonCompliant: 0, skipped: 0 }
        this.scanned = true
        if (isFirst) {
          this.$message.success(this.resultTotal === 0 ? '扫描完成，未发现不合规记录' : `扫描完成，发现 ${this.resultTotal} 条不合规记录`)
        }
      } catch (error) {
        this.$message.error('扫描失败，请稍后重试')
      } finally {
        this.scanning = false
      }
    },

    // 重新扫描：回配置态（保留上次区间）
    rescan () {
      this.scanned = false
      this.resultList = []
      this.resultTotal = 0
      this.summary = { scanned: 0, nonCompliant: 0, skipped: 0 }
      this.pagenum = 1
    },

    // 弹框关闭：重置回配置态（保留时间区间）
    handleClose () {
      this.rescan()
    },

    // 导出结果（xlsx）
    async handleExport () {
      if (this.resultTotal === 0) {
        return this.$message.warning('无不合规记录可导出')
      }
      this.exporting = true
      try {
        const { data } = await this.$http.get('zhaoshang/customerBill/checkComplianceExport', {
          params: {
            startDate: this.dateRange[0],
            endDate: this.dateRange[1]
          },
          responseType: 'blob'
        })
        // 后端错误时返回 JSON（returnRes 恒 HTTP 200，业务码在 meta；Blob.type 含完整 MIME 串带 charset，需前缀匹配）
        if (data.type && data.type.indexOf('application/json') === 0) {
          const err = JSON.parse(await data.text())
          return this.$message.error((err.meta && err.meta.msg) || '导出失败')
        }
        this._downloadBlob(data, `附件合规检测_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`)
      } catch (error) {
        this.$message.error('导出失败，请稍后重试')
      } finally {
        this.exporting = false
      }
    },

    // blob 下载
    _downloadBlob (blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    // 分页
    handleSizeChange (val) {
      this.pagesize = val
      this.pagenum = 1
      this.fetchPage()
    },
    handleCurrentChange (val) {
      this.pagenum = val
      this.fetchPage()
    },

    // 跨页连续序号
    indexMethod (index) {
      return (this.pagenum - 1) * this.pagesize + index + 1
    },

    // 点击项目名：联动财务列表过滤（父组件处理）
    handleProjectClick (row) {
      this.dialogVisible = false
      this.$emit('project-click', row)
    },

    // 附件 URL 拼接
    getFileUrl (url) {
      if (!url) return ''
      return this.base + url
    },

    // 附件预览（图片大图 / PDF 弹窗，与财务列表一致）
    handlePreviewAttachment (file) {
      if (!file) return

      const fileUrl = this.getFileUrl(file.url)
      this.previewTitle = file.name || '附件预览'

      if (file.type === 'image') {
        this.imagePreviewList = [fileUrl]
        this.currentImageUrl = fileUrl
        this.$nextTick(() => {
          this.$refs.imageViewer.clickHandler()
        })
      } else if (file.type === 'pdf') {
        this.previewUrl = fileUrl
        this.pdfPreviewDialogVisible = true
      } else {
        this.$message.info('不支持预览该文件类型')
      }
    }
  },
  watch: {
    // 打开弹框时：重置为配置态、刷新附件基地址、默认当月区间
    visible (val) {
      if (val) {
        this.base = window.sessionStorage.getItem('base') || ''
        this.dateRange = this._defaultMonthRange()
      }
    }
  }
}
</script>

<style scoped>
.rule-text {
  font-size: 12px;
  line-height: 22px;
  color: #606266;
  margin-top: 4px;
}
.text-title {
  font-size: 12px;
  line-height: 18px;
}
.text-content {
  font-size: 12px;
  color: #606266;
  line-height: 18px;
}
.red {
  color: #f56c6c;
}
/* 弹框内容区限高+滚动兜底（窄屏/缩放时内容超高仍可滚动） */
::v-deep .compliance-check-dialog .el-dialog__body {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}
/* 图片大图查看器浮层高于本弹框（弹框已 append-to-body） */
::v-deep .el-image-viewer__wrapper {
  z-index: 3000;
}
</style>
